/** @module Photo and reference side by side, large and zoomed together, with every decision to hand. */
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/client";
import { printedAs, readNumber, useCard } from "@/lib/cards";
import { CardPicker, startingQuery } from "@/components/card-picker";
import {
  FIT,
  panBy,
  STEP,
  stepped,
  transformOf,
  zoomAt,
  type Zoom,
} from "@/features/review/compare-zoom";
import { type Choice, ResolveForm } from "@/components/resolve-form";
import { useAuthedImage } from "@/lib/images";

type Variant = components["schemas"]["CardVariantOut"];

export interface CompareCandidate {
  print_id: string;
  score: number;
  from_ocr?: boolean;
  picked?: boolean;
}

/**
 * One image in the comparison, clipped and transformed.
 *
 * Both panes get the same zoom, so the same corner of both cards is on screen: a border
 * that is a millimetre wider only shows up when you are looking at the same spot.
 */
function Pane({
  src,
  alt,
  caption,
  zoom,
  onZoom,
}: {
  src: string | null;
  alt: string;
  caption: string;
  zoom: Zoom;
  onZoom: (next: Zoom) => void;
}) {
  const box = useRef<HTMLDivElement | null>(null);

  /** Where the pointer sits inside the pane, in fractions of it. */
  const spotOf = (event: { clientX: number; clientY: number }) => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect) return { x: 0.5, y: 0.5 };
    return {
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    };
  };

  const drag = (event: React.PointerEvent) => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect || zoom.scale <= 1) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    let lastX = event.clientX;
    let lastY = event.clientY;
    let current = zoom;
    const move = (step: PointerEvent) => {
      current = panBy(
        current,
        (step.clientX - lastX) / rect.width,
        (step.clientY - lastY) / rect.height,
      );
      lastX = step.clientX;
      lastY = step.clientY;
      onZoom(current);
    };
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  };

  return (
    <figure className="flex min-h-0 min-w-0 flex-1 flex-col gap-1">
      <div
        ref={box}
        data-testid="compare-pane"
        role="button"
        tabIndex={0}
        aria-label={caption}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onZoom(zoom.scale > 1 ? FIT : stepped(zoom, STEP * STEP));
          }
        }}
        onPointerDown={drag}
        onWheel={(event) => {
          onZoom(zoomAt(zoom, event.deltaY < 0 ? STEP : 1 / STEP, spotOf(event)));
        }}
        onDoubleClick={(event) => {
          onZoom(zoom.scale > 1 ? FIT : zoomAt(zoom, STEP * STEP, spotOf(event)));
        }}
        className="relative min-h-0 grow touch-none overflow-hidden rounded-[var(--radius-sm)] bg-(--surface-2)"
        style={{ cursor: zoom.scale > 1 ? "grab" : "zoom-in" }}
      >
        {src ? (
          <img
            src={src}
            alt={alt}
            draggable={false}
            className="absolute inset-0 h-full w-full object-contain select-none"
            style={{ transform: transformOf(zoom), transformOrigin: "center" }}
          />
        ) : null}
      </div>
      <figcaption className="text-center text-[length:var(--text-xs)] text-(--text-muted)">
        {caption}
      </figcaption>
    </figure>
  );
}

/** One candidate to switch to: the picture is the point, the number only confirms it. */
function CandidateTile({
  printId,
  label,
  selected,
  onSelect,
}: {
  printId: string;
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const url = useAuthedImage(`/api/v1/images/cards/${printId}/low`);
  const card = useCard(printId);
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      title={printId}
      className="cand-card w-24 shrink-0 gap-1 p-[6px]"
    >
      <span className="aspect-[63/88] w-full">
        {url ? (
          <img
            src={url}
            alt=""
            className="h-full w-full rounded-[var(--radius-sm)] object-contain"
          />
        ) : (
          <span className="block h-full w-full rounded-[var(--radius-sm)] bg-(--surface-2)" />
        )}
      </span>
      <span className="truncate text-[length:var(--text-xs)] font-medium">
        {card.data?.name ?? printId}
      </span>
      <span className="num t-xs truncate">{printedAs(card.data, "")}</span>
      <span className="num t-xs t-muted">{label}</span>
    </button>
  );
}

export function CompareDialog({
  open,
  onClose,
  photo,
  candidates,
  chosen,
  onChoose,
  variants,
  choice,
  onChoiceChange,
  onConfirm,
  onDismiss,
  busy,
  ocr,
  notice,
}: {
  open: boolean;
  onClose: () => void;
  photo: string | null;
  candidates: CompareCandidate[];
  chosen: string | null;
  onChoose: (printId: string) => void;
  variants: Variant[];
  choice: Choice;
  onChoiceChange: (choice: Choice) => void;
  onConfirm: () => void;
  onDismiss: () => void;
  busy: boolean;
  ocr?: { number?: string; set_code?: string; total?: string };
  /** Shown under the header, e.g. a warning that the number does not match. */
  notice?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const dialog = useRef<HTMLDialogElement | null>(null);
  const chosenCard = useCard(chosen);
  const read = readNumber(ocr);
  const [zoom, setZoom] = useState<Zoom>(FIT);
  const [zoomedFor, setZoomedFor] = useState(chosen);
  const [searching, setSearching] = useState(false);
  // The comparison wants detail, so it asks for the large picture, not the thumbnail.
  const reference = useAuthedImage(chosen && open ? `/api/v1/images/cards/${chosen}/high` : null);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  // A new card is a new comparison; starting zoomed into a corner would be disorienting.
  if (zoomedFor !== chosen) {
    setZoomedFor(chosen);
    setZoom(FIT);
  }

  const step = (factor: number) => {
    setZoom((current) => stepped(current, factor));
  };

  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      onCancel={onClose}
      aria-label={t("review.compare.title")}
      className="m-0 h-dvh max-h-none w-dvw max-w-none bg-(--surface) p-0 text-(--text) backdrop:bg-black/70"
    >
      <div className="flex h-dvh flex-col gap-3 p-3 md:p-4">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="t-h3">
              {chosen
                ? `${chosenCard.data?.name ?? chosen} · ${printedAs(chosenCard.data, chosen)}`
                : t("review.compare.noCard")}
            </h2>
            {read ? (
              <span className="text-[length:var(--text-sm)] text-(--text-muted)">
                {t("review.ocr", { number: read })}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={t("review.compare.zoomOut")}
              onClick={() => {
                step(1 / STEP);
              }}
              className="icon-btn text-[length:var(--text-lg)]"
            >
              −
            </button>
            <span className="tabular w-12 text-center text-[length:var(--text-sm)] text-(--text-muted)">
              {zoom.scale.toFixed(1)}×
            </span>
            <button
              type="button"
              aria-label={t("review.compare.zoomIn")}
              onClick={() => {
                step(STEP);
              }}
              className="icon-btn text-[length:var(--text-lg)]"
            >
              +
            </button>
            <button
              type="button"
              onClick={() => {
                setZoom(FIT);
              }}
              className="btn btn-secondary btn-sm"
            >
              {t("review.compare.fit")}
            </button>
            <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
              {t("common.close")}
            </button>
          </div>
        </header>
        {notice}

        {/* Side by side where there is room, stacked on a phone; both panes stay equal. */}
        <div className="flex min-h-0 grow flex-col gap-3 md:flex-row">
          <Pane
            src={photo}
            alt={t("review.yourPhoto")}
            caption={t("review.yourPhoto")}
            zoom={zoom}
            onZoom={setZoom}
          />
          <Pane
            src={reference}
            alt={chosen ?? ""}
            caption={t("review.compare.reference")}
            zoom={zoom}
            onZoom={setZoom}
          />
        </div>

        <div className="flex shrink-0 flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[length:var(--text-sm)] text-(--text-muted)">
              {t("review.compare.switch")}
            </span>
            <button
              type="button"
              aria-pressed={searching}
              onClick={() => {
                setSearching((on) => !on);
              }}
              className="chip"
            >
              {searching ? t("review.compare.closeSearch") : t("review.compare.search")}
            </button>
          </div>

          {searching ? (
            <CardPicker
              initialQuery={startingQuery(ocr)}
              layout="grid"
              onPick={(printId) => {
                onChoose(printId);
                setSearching(false);
              }}
            />
          ) : (
            <ul className="flex gap-2 overflow-x-auto pb-1">
              {candidates.map((candidate) => (
                <li key={candidate.print_id}>
                  <CandidateTile
                    printId={candidate.print_id}
                    label={
                      candidate.picked
                        ? t("review.picked")
                        : candidate.score.toLocaleString(i18n.resolvedLanguage ?? "de", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                    }
                    selected={chosen === candidate.print_id}
                    onSelect={() => {
                      onChoose(candidate.print_id);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="shrink-0">
          <ResolveForm
            variants={variants}
            choice={choice}
            locale={i18n.resolvedLanguage ?? "de"}
            onChange={onChoiceChange}
          />
        </div>

        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            disabled={!chosen || choice.variantId === null || busy}
            onClick={onConfirm}
            className="btn btn-primary grow"
          >
            {t("review.confirm")}
          </button>
          <button type="button" disabled={busy} onClick={onDismiss} className="btn btn-secondary">
            {t("review.dismiss")}
          </button>
        </div>
      </div>
    </dialog>
  );
}
